import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  Filter,
  Plus,
  BookOpen,
  Trash2,
  CheckCircle2,
  Target,
  Pencil
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { PersonalTaskEditDialog } from "../components/PersonalTaskEditDialog";
import { SECONDARY_BTN, PRIMARY_BTN, GHOST_BTN, FIELD_CLS, SectionCard } from "../components/designSystem";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call",
  script_videomaker: "Script VM",
  script_cliente: "Script Cl.",
  conferma_script: "Conferma",
  ped_programmazione: "PED",
  avvio_adv: "ADV",
  appuntamento_successivo: "App.",
  report_fine_mese: "Report",
  compilare_statistiche_adv: "Stats ADV",
  editing_video: "Editing",
  allineamento_strategico_tiktok: "Allin. TikTok",
  call_settimanale_tiktok: "Call TikTok",
  check_adv_tiktok: "Check ADV TT",
  check_affiliazioni_live_tiktok: "Check Affil. TT"
};

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const DAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const SELECT_TRIGGER_CLS = `${FIELD_CLS} w-48 gap-1.5`;

function IconBtn({ icon: Icon, onClick, testId, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="w-8 h-8 rounded-[10px] border border-[#e0e0ea] bg-white flex items-center justify-center text-[#565d6d] hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors disabled:opacity-50 shrink-0"
    >
      <Icon className="w-4 h-4" strokeWidth={1.9} />
    </button>
  );
}

export default function CalendarPage({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [personalTasks, setPersonalTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  // ?client= arriva dal pulsante "cliente non gestito" e dall'email di presa in carico.
  const [filterClient, setFilterClient] = useState(searchParams.get("client") || "all");
  const [filterSmm, setFilterSmm] = useState("all");
  const [filterPm, setFilterPm] = useState("all");
  const [clients, setClients] = useState([]);
  const [smmUsers, setSmmUsers] = useState([]);
  const [pmUsers, setPmUsers] = useState([]);

  // Drag and drop state
  const [draggingTask, setDraggingTask] = useState(null);

  // Personal task dialog
  const [showAddPersonalTask, setShowAddPersonalTask] = useState(false);
  const [newPersonalTask, setNewPersonalTask] = useState({ title: "", description: "", due_date: "", user_id: "", client_id: "" });
  const [savingPersonalTask, setSavingPersonalTask] = useState(false);

  // View filter: all, pm_only (call obiettivo), personal_only
  const [viewFilter, setViewFilter] = useState("all");

  // Edit personal task
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Call obiettivo data
  const [callObiettivo, setCallObiettivo] = useState([]);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale";
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm");

  useEffect(() => {
    fetchData();
  }, []);

  // Presa in carico: con ?nuovo_task=1 apre subito il dialog del task già sul
  // cliente, poi toglie il parametro così un refresh non lo riapre.
  useEffect(() => {
    const clientId = searchParams.get("client");
    if (clientId) setFilterClient(clientId);
    if (clientId && searchParams.get("nuovo_task") === "1") {
      setNewPersonalTask(prev => ({ ...prev, client_id: clientId }));
      setShowAddPersonalTask(true);
      const next = new URLSearchParams(searchParams);
      next.delete("nuovo_task");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [tasksRes, clientsRes, smmRes, personalRes, pmRes] = await Promise.all([
        apiFetch("/tasks"),
        apiFetch("/clients"),
        apiFetch("/users/smm"),
        apiFetch("/personal-tasks"),
        apiFetch("/users/pm")
      ]);

      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
        // Extract call obiettivo dates
        const callDates = clientsData
          .filter(c => c.data_call_obiettivo)
          .map(c => ({
            client_id: c.client_id,
            client_name: c.name,
            date: c.data_call_obiettivo,
            type: "call_obiettivo"
          }));
        setCallObiettivo(callDates);
      }
      if (smmRes.ok) setSmmUsers(await smmRes.json());
      if (personalRes.ok) setPersonalTasks(await personalRes.json());
      if (pmRes.ok) setPmUsers(await pmRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggingTask(task);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.task_id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, date) => {
    e.preventDefault();
    if (!draggingTask) return;

    const newDateStr = formatDateLocal(date);

    try {
      const response = await apiFetch(`/personal-tasks/${draggingTask.task_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ due_date: newDateStr })
      });

      if (response.ok) {
        toast.success("Task spostato!");
        fetchData();
      } else {
        toast.error("Errore nello spostamento");
      }
    } catch (error) {
      toast.error("Errore nello spostamento del task");
    } finally {
      setDraggingTask(null);
    }
  };

  // Save personal task
  const handleSavePersonalTask = async () => {
    if (!newPersonalTask.title.trim()) {
      toast.error("Inserisci un titolo per il task");
      return;
    }
    if (!newPersonalTask.due_date) {
      toast.error("Seleziona una data di scadenza");
      return;
    }
    if (!newPersonalTask.client_id) {
      toast.error("Seleziona un cliente - il cliente è obbligatorio");
      return;
    }

    setSavingPersonalTask(true);
    try {
      const response = await apiFetch("/personal-tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newPersonalTask)
      });

      if (response.ok) {
        toast.success("Task personale creato!");
        // Il pulsante "cliente non gestito" si ricontrolla subito, senza aspettare il polling.
        window.dispatchEvent(new Event("presa-in-carico:aggiorna"));
        setShowAddPersonalTask(false);
        setNewPersonalTask({ title: "", description: "", due_date: "", user_id: "", client_id: "" });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore nella creazione del task");
    } finally {
      setSavingPersonalTask(false);
    }
  };

  // Delete personal task
  const handleDeletePersonalTask = async (taskId) => {
    try {
      const response = await apiFetch(`/personal-tasks/${taskId}`, { method: "DELETE" });
      if (response.ok) {
        toast.success("Task eliminato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Complete personal task
  const handleCompletePersonalTask = async (taskId) => {
    try {
      const response = await apiFetch(`/personal-tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "completato" })
      });
      if (response.ok) {
        toast.success("Task completato!");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Build client to SMM map
  const clientSmmMap = {};
  clients.forEach(c => {
    clientSmmMap[c.client_id] = c.assigned_smm_id;
  });

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Get the day of week for the first day (0 = Sunday, we want 0 = Monday)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    return { daysInMonth, startDayOfWeek };
  };

  // Helper to format date as YYYY-MM-DD in local timezone (avoiding UTC conversion issues)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to get local date from ISO string (handles timezone correctly)
  // IMPORTANT: Extract date directly from string to avoid UTC timezone conversion issues
  const getLocalDateStr = (isoString) => {
    if (!isoString) return "";
    // If it's already a YYYY-MM-DD string, return as-is
    if (typeof isoString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
      return isoString;
    }
    // For ISO strings like "2025-02-28T00:00:00.000Z", extract just the date part
    // This avoids timezone conversion that could shift the day
    if (typeof isoString === "string" && isoString.includes("T")) {
      return isoString.split("T")[0];
    }
    // Fallback for other formats
    const date = new Date(isoString);
    return formatDateLocal(date);
  };

  const getTasksForDate = (date) => {
    const dateStr = formatDateLocal(date);

    // If view filter is personal_only or pm_only, don't show regular tasks
    if (viewFilter === "personal_only" || viewFilter === "pm_only") {
      return [];
    }

    return tasks.filter(task => {
      const taskDateStr = getLocalDateStr(task.due_date);
      const matchesClient = filterClient === "all" || task.client_id === filterClient;
      const taskSmmId = clientSmmMap[task.client_id];
      const matchesSmm = filterSmm === "all" || taskSmmId === filterSmm;
      // Filter by PM (based on client's assigned_pm_id)
      const client = clients.find(c => c.client_id === task.client_id);
      const matchesPm = filterPm === "all" || client?.assigned_pm_id === filterPm;
      return taskDateStr === dateStr && matchesClient && matchesSmm && matchesPm;
    });
  };

  const getPersonalTasksForDate = (date) => {
    const dateStr = formatDateLocal(date);

    // If view filter is pm_only, don't show personal tasks
    if (viewFilter === "pm_only") {
      return [];
    }

    return personalTasks.filter(task => {
      const taskDateStr = getLocalDateStr(task.due_date);
      // Filter by SMM if selected
      const matchesSmm = filterSmm === "all" || task.assigned_to_id === filterSmm || task.user_id === filterSmm;
      // Filter by PM (based on who created the task or assigned_to)
      const matchesPm = filterPm === "all" || task.created_by_id === filterPm;
      return taskDateStr === dateStr && matchesSmm && matchesPm;
    });
  };

  const getCallObiettivoForDate = (date) => {
    const dateStr = formatDateLocal(date);

    // Only show if view is all or pm_only
    if (viewFilter === "personal_only") {
      return [];
    }

    return callObiettivo.filter(item => {
      const itemDateStr = getLocalDateStr(item.date);
      const matchesClient = filterClient === "all" || item.client_id === filterClient;
      // Filter by PM (based on client's assigned_pm_id)
      const client = clients.find(c => c.client_id === item.client_id);
      const matchesPm = filterPm === "all" || client?.assigned_pm_id === filterPm;
      return itemDateStr === dateStr && matchesClient && matchesPm;
    });
  };

  const getSemaforoForDate = (date) => {
    const dayTasks = getTasksForDate(date);
    if (dayTasks.length === 0) return null;

    // Priority: scaduto > rosso > giallo > verde
    if (dayTasks.some(t => t.semaforo === "scaduto" && t.status !== "completato")) return "scaduto";
    if (dayTasks.some(t => t.semaforo === "rosso" && t.status !== "completato")) return "rosso";
    if (dayTasks.some(t => t.semaforo === "giallo" && t.status !== "completato")) return "giallo";
    if (dayTasks.some(t => t.semaforo === "verde" && t.status !== "completato")) return "verde";
    return "completato";
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDate(null);
  };

  const { daysInMonth, startDayOfWeek } = getDaysInMonth(currentDate);

  const getSemaforoBadge = (semaforo, status) => {
    if (status === "completato") {
      return <Badge className="bg-[#f3f3f8] text-[#6b7280] border-0 text-xs">✓</Badge>;
    }
    const colors = {
      verde: "#12a05c",
      giallo: "#e0a12b",
      rosso: "#c0392b",
      scaduto: "#7e8494"
    };
    return <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colors[semaforo] || "#c3c6d0" }}></span>;
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-96 bg-muted rounded-xl"></div>
      </div>
    );
  }

  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];
  const selectedDatePersonalTasks = selectedDate ? getPersonalTasksForDate(selectedDate) : [];
  const selectedDateCallObiettivo = selectedDate ? getCallObiettivoForDate(selectedDate) : [];

  // Open add personal task dialog with selected date
  const handleAddPersonalTaskForDate = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    setNewPersonalTask({ title: "", description: "", due_date: dateStr });
    setShowAddPersonalTask(true);
  };

  return (
    <div className="space-y-[18px] animate-fade-in scheda-font-ui" data-testid="calendar-page">
      {/* Testata */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-[30px] font-extrabold leading-[1.15] tracking-[-0.02em] text-[#15161c]">
            Calendario
          </h1>
          <p className="text-[13px] text-[#6b7280] mt-1">
            Vista mensile delle scadenze
          </p>
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={() => setShowAddPersonalTask(true)}
            className={PRIMARY_BTN}
            data-testid="add-personal-task-btn"
          >
            <Plus className="w-4 h-4" />
            Task Personale
          </button>

          {isPM && (
            <Select value={filterPm} onValueChange={setFilterPm}>
              <SelectTrigger className={SELECT_TRIGGER_CLS} data-testid="filter-pm-calendar">
                <Users className="w-3.5 h-3.5 text-[#9095a1]" />
                <SelectValue placeholder="Tutti i PM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i PM</SelectItem>
                {pmUsers.map(pm => (
                  <SelectItem key={pm.user_id} value={pm.user_id}>
                    {pm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={viewFilter} onValueChange={setViewFilter}>
            <SelectTrigger className={SELECT_TRIGGER_CLS} data-testid="filter-view-type">
              <CalendarIcon className="w-3.5 h-3.5 text-[#9095a1]" />
              <SelectValue placeholder="Tipo vista" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le task</SelectItem>
              <SelectItem value="pm_only">Solo Call Obiettivo</SelectItem>
              <SelectItem value="personal_only">Solo Task Personali</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className={SELECT_TRIGGER_CLS} data-testid="filter-client-calendar">
              <Filter className="w-3.5 h-3.5 text-[#9095a1]" />
              <SelectValue placeholder="Tutti i clienti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {clients.map(client => (
                <SelectItem key={client.client_id} value={client.client_id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isPM && (
            <Select value={filterSmm} onValueChange={setFilterSmm}>
              <SelectTrigger className={SELECT_TRIGGER_CLS} data-testid="filter-smm-calendar">
                <Users className="w-3.5 h-3.5 text-[#9095a1]" />
                <SelectValue placeholder="Tutti gli SMM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli SMM</SelectItem>
                {smmUsers.map(smm => (
                  <SelectItem key={smm.user_id} value={smm.user_id}>
                    {smm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-[18px] items-start">
        {/* Calendario */}
        <div className="lg:col-span-2 bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <div className="flex items-center justify-between px-[26px] pt-[22px] pb-[16px]">
            <IconBtn icon={ChevronLeft} onClick={() => navigateMonth(-1)} testId="prev-month-btn" />
            <h2 className="text-[16.5px] font-bold text-[#15161c]">
              {MONTHS_IT[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <IconBtn icon={ChevronRight} onClick={() => navigateMonth(1)} testId="next-month-btn" />
          </div>

          <div className="px-[26px] pb-[26px]">
            {/* Intestazione giorni */}
            <div className="grid grid-cols-7 gap-1 mb-1.5">
              {DAYS_IT.map(day => (
                <div
                  key={day}
                  className="text-center text-[10.5px] font-bold uppercase tracking-[.06em] text-[#9095a1] p-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Griglia calendario */}
            <div className="grid grid-cols-7 gap-1">
              {[...Array(startDayOfWeek)].map((_, i) => (
                <div key={`empty-${i}`} className="p-2"></div>
              ))}

              {[...Array(daysInMonth)].map((_, i) => {
                const day = i + 1;
                const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                const isToday = new Date().toDateString() === date.toDateString();
                const isSelected = selectedDate?.toDateString() === date.toDateString();
                const dayTasks = getTasksForDate(date);
                const dayPersonalTasks = getPersonalTasksForDate(date);
                const dayCallObiettivo = getCallObiettivoForDate(date);
                const totalTasks = dayTasks.length + dayPersonalTasks.length + dayCallObiettivo.length;

                return (
                  <div
                    key={day}
                    onClick={() => setSelectedDate(date)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, date)}
                    className={`
                      relative p-2 min-h-[64px] text-left rounded-[12px] transition-all cursor-pointer border
                      ${isToday ? "bg-[#eeeefc] border-[#c9c9f0]" : "border-transparent hover:bg-[#fbfbfd] hover:border-[#eeeef5]"}
                      ${isSelected ? "ring-2 ring-[#5b5bd6] ring-offset-2" : ""}
                      ${draggingTask ? "hover:bg-[#f3f0ff] hover:border-[#c9c9f0] hover:border-dashed hover:border-2" : ""}
                    `}
                    data-testid={`calendar-day-${day}`}
                  >
                    <span className={`text-[13.5px] font-semibold ${isToday ? "text-[#5b5bd6]" : "text-[#15161c]"}`}>
                      {day}
                    </span>

                    {totalTasks > 0 && (
                      <div className="mt-1 flex flex-wrap gap-0.5">
                        {dayCallObiettivo.slice(0, 2).map((item, idx) => (
                          <span key={`call-${idx}`} className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]"></span>
                        ))}
                        {dayTasks.slice(0, 3 - dayCallObiettivo.length).map((task, idx) => {
                          const color = {
                            verde: "#12a05c",
                            giallo: "#e0a12b",
                            rosso: "#c0392b",
                            scaduto: "#9095a1"
                          }[task.status === "completato" ? "verde" : task.semaforo];

                          return (
                            <span
                              key={`task-${idx}`}
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ background: color, opacity: task.status === "completato" ? 0.5 : 1 }}
                            ></span>
                          );
                        })}
                        {dayPersonalTasks.slice(0, Math.max(0, 3 - dayTasks.length - dayCallObiettivo.length)).map((task, idx) => (
                          <span
                            key={`personal-${idx}`}
                            className="w-1.5 h-1.5 rounded-full bg-[#5b5bd6]"
                            style={{ opacity: task.status === "completato" ? 0.5 : 1 }}
                          ></span>
                        ))}
                        {totalTasks > 3 && (
                          <span className="text-[10px] text-[#9095a1] scheda-font-mono">+{totalTasks - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legenda */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#f0f0f6]">
              <div className="flex items-center gap-1.5 text-[12px] text-[#6b7280]">
                <span className="w-2 h-2 rounded-full" style={{ background: "#12a05c" }}></span>
                In tempo
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-[#6b7280]">
                <span className="w-2 h-2 rounded-full" style={{ background: "#e0a12b" }}></span>
                Attenzione
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-[#6b7280]">
                <span className="w-2 h-2 rounded-full" style={{ background: "#c0392b" }}></span>
                Oggi
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-[#6b7280]">
                <span className="w-2 h-2 rounded-full" style={{ background: "#9095a1" }}></span>
                Scaduto
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-[#6b7280]">
                <span className="w-2 h-2 rounded-full bg-[#5b5bd6]"></span>
                Personale
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-[#6b7280]">
                <span className="w-2 h-2 rounded-full bg-[#3b82f6]"></span>
                Call Obiettivo
              </div>
            </div>
          </div>
        </div>

        {/* Giorno selezionato */}
        <SectionCard
          icon={CalendarIcon}
          iconBg="#eeeefc"
          iconColor="#5b5bd6"
          title={selectedDate
            ? selectedDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })
            : "Seleziona un giorno"}
          action={selectedDate ? (
            <button className={SECONDARY_BTN} style={{ width: 32, height: 32, padding: 0 }} onClick={() => handleAddPersonalTaskForDate(selectedDate)} data-testid="add-task-for-date-btn">
              <Plus className="w-3.5 h-3.5" />
            </button>
          ) : null}
        >
          {!selectedDate ? (
            <p className="text-[13.5px] text-[#9095a1]">
              Clicca su un giorno per vedere i task
            </p>
          ) : (selectedDateTasks.length === 0 && selectedDatePersonalTasks.length === 0 && selectedDateCallObiettivo.length === 0) ? (
            <div className="text-center py-4">
              <p className="text-[13.5px] text-[#9095a1] mb-3">
                Nessun task per questa data
              </p>
              <button className={SECONDARY_BTN} onClick={() => handleAddPersonalTaskForDate(selectedDate)}>
                <Plus className="w-3.5 h-3.5" />
                Aggiungi Task
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {/* Call Obiettivo */}
              {selectedDateCallObiettivo.length > 0 && (
                <>
                  <p className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#3b82f6] mb-2 flex items-center gap-1.5">
                    <Target className="w-3 h-3" />
                    Call Obiettivo
                  </p>
                  {selectedDateCallObiettivo.map((item, index) => (
                    <div
                      key={`call-${index}`}
                      className="p-3 rounded-[12px] border border-blue-200 bg-blue-50/70"
                      data-testid={`selected-day-call-${index}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-[13.5px] text-blue-700">{item.client_name}</p>
                          <p className="text-[12px] text-blue-600 mt-0.5">
                            Call Obiettivo Cliente
                          </p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">PM</Badge>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Task clienti */}
              {selectedDateTasks.length > 0 && selectedDateCallObiettivo.length > 0 && (
                <div className="border-t border-[#f0f0f6] pt-2.5 mt-2.5">
                  <p className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1] mb-2">
                    Task Clienti
                  </p>
                </div>
              )}
              {selectedDateTasks.map((task, index) => (
                <div
                  key={task.task_id}
                  onClick={() => navigate(`/task/${task.task_id}`)}
                  className="p-3 rounded-[12px] border border-[#eeeef5] hover:bg-[#fbfbfd] hover:border-[#e9e9f1] cursor-pointer transition-colors"
                  data-testid={`selected-day-task-${index}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[13.5px] text-[#15161c]">{task.client_name}</p>
                      <p className="text-[12px] text-[#9095a1] mt-0.5">
                        {etichettaTask(task, TASK_TYPE_LABELS)}
                      </p>
                    </div>
                    {getSemaforoBadge(task.semaforo, task.status)}
                  </div>
                </div>
              ))}

              {/* Task personali */}
              {selectedDatePersonalTasks.length > 0 && (
                <>
                  {(selectedDateTasks.length > 0 || selectedDateCallObiettivo.length > 0) && (
                    <div className="border-t border-[#f0f0f6] pt-2.5 mt-2.5">
                      <p className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#5b5bd6] mb-2 flex items-center gap-1.5">
                        <BookOpen className="w-3 h-3" />
                        Task Personali (trascina per spostare)
                      </p>
                    </div>
                  )}
                  {selectedDatePersonalTasks.map((task, index) => (
                    <div
                      key={task.task_id}
                      draggable={task.status !== "completato"}
                      onDragStart={(e) => handleDragStart(e, task)}
                      className={`p-3 rounded-[12px] border border-[#e5e5f4] bg-[#f8f8fc] ${
                        task.status !== "completato" ? "cursor-grab active:cursor-grabbing" : ""
                      }`}
                      data-testid={`selected-day-personal-task-${index}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <Badge className="mb-1 bg-[#eeeefc] text-[#5b5bd6] border-0 font-bold text-xs">
                            {task.client_name || "Cliente non assegnato"}
                          </Badge>
                          <p className="font-semibold text-[13.5px] text-[#15161c]">{task.title}</p>
                          {task.description && (
                            <p className="text-[12px] text-[#9095a1] mt-0.5">
                              {task.description}
                            </p>
                          )}
                          {task.smm_name && (
                            <p className="text-[12px] text-[#5b5bd6] mt-1">
                              Assegnato a: {task.smm_name}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          {task.status !== "completato" && (
                            <button
                              className="h-7 w-7 rounded-[8px] flex items-center justify-center text-[#5b5bd6] hover:bg-[#eeeefc] transition-colors"
                              onClick={(e) => { e.stopPropagation(); setEditingTask(task); setShowEditDialog(true); }}
                              data-testid={`edit-sidebar-task-${task.task_id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {task.status !== "completato" && (
                            <button
                              className="h-7 w-7 rounded-[8px] flex items-center justify-center text-[#0f7040] hover:bg-[#e7f7ee] transition-colors"
                              onClick={(e) => { e.stopPropagation(); handleCompletePersonalTask(task.task_id); }}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {task.status === "completato" && (
                            <button
                              className="h-7 w-7 rounded-[8px] flex items-center justify-center text-[#5b5bd6] hover:bg-[#eeeefc] transition-colors"
                              onClick={(e) => { e.stopPropagation(); setEditingTask(task); setShowEditDialog(true); }}
                              data-testid={`edit-sidebar-completed-task-${task.task_id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            className="h-7 w-7 rounded-[8px] flex items-center justify-center text-[#c0392b] hover:bg-[#fdecea] transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleDeletePersonalTask(task.task_id); }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {task.status === "completato" && (
                        <Badge className="mt-2 bg-[#f3f3f8] text-[#6b7280] border-0 text-xs">Completato</Badge>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Nuovo Task Personale */}
      <Dialog open={showAddPersonalTask} onOpenChange={setShowAddPersonalTask}>
        <DialogContent className="sm:max-w-md scheda-font-ui">
          <DialogHeader>
            <DialogTitle className="text-[16.5px] font-bold text-[#15161c]">Nuovo Task Personale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Cliente selector - OBBLIGATORIO */}
            <div className="space-y-1.5">
              <Label htmlFor="task-client" className="flex items-center gap-1 text-[12.5px] font-semibold text-[#565d6d]">
                Cliente <span className="text-[#c0392b]">*</span>
              </Label>
              <Select
                value={newPersonalTask.client_id}
                onValueChange={(value) => setNewPersonalTask({...newPersonalTask, client_id: value})}
              >
                <SelectTrigger data-testid="personal-task-client-select" className={`${FIELD_CLS} ${!newPersonalTask.client_id ? "border-[#f3c9c3]" : ""}`}>
                  <SelectValue placeholder="Seleziona cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11.5px] text-[#9095a1]">Il cliente è obbligatorio per ogni task personale</p>
            </div>
            {/* SMM selector for PM */}
            {isPM && (
              <div className="space-y-1.5">
                <Label htmlFor="task-smm" className="text-[12.5px] font-semibold text-[#565d6d]">Assegna a SMM</Label>
                <Select
                  value={newPersonalTask.user_id || "self"}
                  onValueChange={(value) => setNewPersonalTask({...newPersonalTask, user_id: value === "self" ? "" : value})}
                >
                  <SelectTrigger data-testid="personal-task-smm-select" className={FIELD_CLS}>
                    <SelectValue placeholder="Seleziona SMM..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Me stesso</SelectItem>
                    {smmUsers.map(smm => (
                      <SelectItem key={smm.user_id} value={smm.user_id}>
                        {smm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="task-title" className="text-[12.5px] font-semibold text-[#565d6d]">Titolo *</Label>
              <Input
                id="task-title"
                placeholder="Es: Chiamare cliente X"
                value={newPersonalTask.title}
                onChange={(e) => setNewPersonalTask({...newPersonalTask, title: e.target.value})}
                className={FIELD_CLS}
                data-testid="personal-task-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-description" className="text-[12.5px] font-semibold text-[#565d6d]">Descrizione (opzionale)</Label>
              <Textarea
                id="task-description"
                placeholder="Dettagli aggiuntivi..."
                value={newPersonalTask.description}
                onChange={(e) => setNewPersonalTask({...newPersonalTask, description: e.target.value})}
                rows={3}
                className="rounded-[10px] border-[#e4e4ed] shadow-none focus-visible:ring-0 focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14)] scheda-font-ui"
                data-testid="personal-task-description-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-due-date" className="text-[12.5px] font-semibold text-[#565d6d]">Data di scadenza *</Label>
              <Input
                id="task-due-date"
                type="date"
                value={newPersonalTask.due_date}
                onChange={(e) => setNewPersonalTask({...newPersonalTask, due_date: e.target.value})}
                className={`${FIELD_CLS} scheda-font-mono`}
                data-testid="personal-task-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <button className={GHOST_BTN} onClick={() => setShowAddPersonalTask(false)}>
              Annulla
            </button>
            <button
              onClick={handleSavePersonalTask}
              disabled={savingPersonalTask || !newPersonalTask.client_id}
              className={PRIMARY_BTN}
              data-testid="save-personal-task-btn"
            >
              {savingPersonalTask ? "Salvataggio..." : "Salva Task"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modifica Task Personale */}
      <PersonalTaskEditDialog
        task={editingTask}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSaved={fetchData}
      />
    </div>
  );
}
